import { useState } from 'react'
import { useStore } from 'react-redux'
import { Alert, Button, Checkbox, Dialog, Link, StepDialog, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { FinancialAccount } from '../../../reducers/finances/financialAccountsReducer'
import { FinancialAccountWithAdminInfo } from '../../../reducers/admin/financialAccountsReducer'
import {
  bulkUpdateTransactionsByAccount,
  BULK_UPDATE_TRANSACTIONS_BY_ACCOUNT_KEY,
} from '../../../actions/adminActions'
import { getFetchError } from '../../../reducers/fetch'
import { TransactionRuleCategoryType } from '../../../reducers/admin/transactionRulesReducer'
import { useReselector } from '../../../utils/sharedHooks'
import { useAppDispatch, ReduxState } from '../../../utils/typeHelpers'

interface MarkInactiveStepProps {
  account: FinancialAccount | FinancialAccountWithAdminInfo
  markTransactionsAsPersonal: boolean
  onMarkTransactionsAsPersonalChange: (checked: boolean) => void
  onCancel: () => void
  onConfirm: () => void
  errorMessage?: string
}

const MarkInactiveStep = ({
  account,
  markTransactionsAsPersonal,
  onMarkTransactionsAsPersonalChange,
  onCancel,
  onConfirm,
  errorMessage,
}: MarkInactiveStepProps) => (
  <>
    {account.plaidInstitutionName && (
      <Text variant={TypographyStyles.BODY_XS} className="mt-1">
        {account.plaidInstitutionName} — {account.name}: {account.mask}
      </Text>
    )}
    <Text variant={TypographyStyles.BODY_SM} className="mt-4">
      Are you sure you want to mark this account as inactive? This is a
      permanent change.
    </Text>
    <div className="mt-4">
      <Checkbox
        checked={markTransactionsAsPersonal}
        onCheckedChange={onMarkTransactionsAsPersonalChange}
        label="Mark all transactions as personal"
      />
    </div>
    {errorMessage && (
      <Alert variant="error" description={errorMessage} className="mt-4" />
    )}
    <Dialog.Footer className="mt-6">
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="danger" onClick={onConfirm}>
        Mark Inactive
      </Button>
    </Dialog.Footer>
  </>
)

interface ConfirmedTransactionsBlockedStepProps {
  userId: number
  message?: string
  onAcknowledge: () => void
}

const ConfirmedTransactionsBlockedStep = ({
  userId,
  message,
  onAcknowledge,
}: ConfirmedTransactionsBlockedStepProps) => (
  <>
    <Text variant={TypographyStyles.BODY_SM} className="mt-4">
      {message ??
        'This account has confirmed transactions that must be unconfirmed first.'}
    </Text>
    <Link
      href={`/admin/finances/transactions/${userId}`}
      newPage
      className="mt-2 inline-block"
    >
      View transactions
    </Link>
    <Dialog.Footer className="mt-6">
      <Button variant="primary" onClick={onAcknowledge}>
        Close
      </Button>
    </Dialog.Footer>
  </>
)

interface MarkInactiveDialogProps {
  open: boolean
  account: FinancialAccount | FinancialAccountWithAdminInfo
  userId: number
  onClose: () => void
  /**
   * Marks the account inactive. Called only after the (optional) "mark all
   * transactions as personal" step succeeds — the dialog handles that step and
   * its confirmed-transactions guard internally.
   */
  onMarkInactive: () => Promise<void> | void
}

export const MarkInactiveDialog = ({
  open,
  account,
  userId,
  onClose,
  onMarkInactive,
}: MarkInactiveDialogProps) => {
  const dispatch = useAppDispatch()
  const store = useStore<ReduxState>()
  const [activeStep, setActiveStep] = useState(0)
  const [markTransactionsAsPersonal, setMarkTransactionsAsPersonal] =
    useState(false)
  const bulkUpdateError = useReselector(
    getFetchError,
    BULK_UPDATE_TRANSACTIONS_BY_ACCOUNT_KEY
  )

  const handleClose = () => {
    setActiveStep(0)
    setMarkTransactionsAsPersonal(false)
    onClose()
  }

  const handleConfirm = async () => {
    if (markTransactionsAsPersonal) {
      const result = await bulkUpdateTransactionsByAccount(account.id, {
        type: TransactionRuleCategoryType.personal,
      })(dispatch)
      if (!result) {
        const error = getFetchError(
          store.getState(),
          BULK_UPDATE_TRANSACTIONS_BY_ACCOUNT_KEY
        )
        if (error?.statusCode === 409) {
          setActiveStep(1)
        }
        return
      }
    }

    await onMarkInactive()
    handleClose()
  }

  return (
    <StepDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose()
      }}
      title="Mark Account as Inactive"
      activeStep={activeStep}
      steps={[
        {
          content: (
            <MarkInactiveStep
              account={account}
              markTransactionsAsPersonal={markTransactionsAsPersonal}
              onMarkTransactionsAsPersonalChange={setMarkTransactionsAsPersonal}
              onCancel={handleClose}
              onConfirm={handleConfirm}
              errorMessage={
                bulkUpdateError?.statusCode === 409
                  ? undefined
                  : bulkUpdateError?.message
              }
            />
          ),
        },
        {
          content: (
            <ConfirmedTransactionsBlockedStep
              userId={userId}
              message={bulkUpdateError?.message}
              onAcknowledge={handleClose}
            />
          ),
        },
      ]}
    />
  )
}
